import { z } from 'zod';
import { IdSchema, MoneySchema, E164Schema, TimestampSchema } from './common.js';
import { TrunkLiveSchema } from './carriers.js';

/**
 * Product catalogue: what sales sells and billing charges for.
 *
 * Extension types are DATA, not a UI enum. "Enterprise" is a priced product
 * with a feature set, and both the invoice line and the switch's entitlement
 * check have to agree on what it includes.
 */

export const FeatureSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string(),
  unit_cost: MoneySchema,
});
export type Feature = z.infer<typeof FeatureSchema>;

export const ExtensionTypeSchema = z.object({
  id: IdSchema,
  reseller_id: IdSchema.nullable(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  monthly_price: MoneySchema,
  setup_price: MoneySchema,
  currency: z.string().length(3),
  max_concurrent_calls: z.number().int().nullable(),
  is_active: z.boolean(),
  sort_order: z.number().int(),
  features: z.array(z.string()),
  /** Sum of the unit costs of its features — the per-seat margin floor. */
  feature_cost: MoneySchema.optional(),
  /** How many extensions currently use it; blocks careless deletion. */
  in_use_count: z.number().int().optional(),
});
export type ExtensionType = z.infer<typeof ExtensionTypeSchema>;

export const ExtensionTypeUpsertSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'lowercase letters, digits and underscores'),
  name: z.string().min(1).max(80),
  description: z.string().max(400).optional(),
  monthly_price: MoneySchema.default('0'),
  setup_price: MoneySchema.default('0'),
  currency: z.string().length(3).default('ZAR'),
  max_concurrent_calls: z.number().int().min(1).max(1000).nullable().optional(),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().min(0).max(9999).default(100),
  features: z.array(z.string().max(48)).default([]),
}).strict();
export type ExtensionTypeUpsert = z.infer<typeof ExtensionTypeUpsertSchema>;

export const CallPlanSchema = z.object({
  id: IdSchema,
  reseller_id: IdSchema.nullable(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sell_rate_deck_id: IdSchema.nullable(),
  rate_deck_name: z.string().nullable(),
  monthly_fee: MoneySchema,
  included_minutes: z.number().int(),
  currency: z.string().length(3),
  billing_mode: z.enum(['prepaid', 'postpaid']),
  is_active: z.boolean(),
  is_default: z.boolean(),
  tenant_count: z.number().int().optional(),
});
export type CallPlan = z.infer<typeof CallPlanSchema>;

export const CallPlanUpsertSchema = z.object({
  key: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,40}$/),
  name: z.string().min(1).max(120),
  description: z.string().max(400).optional(),
  sell_rate_deck_id: IdSchema.optional(),
  monthly_fee: MoneySchema.default('0'),
  included_minutes: z.number().int().min(0).max(1000000).default(0),
  currency: z.string().length(3).default('ZAR'),
  billing_mode: z.enum(['prepaid', 'postpaid']).default('prepaid'),
  is_active: z.boolean().default(true),
}).strict();

/**
 * A trunk assigned to a tenant. Several carriers per tenant is the normal case,
 * not an edge case — priority is what makes failover deterministic.
 */
export const TenantTrunkSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  trunk_id: IdSchema,
  trunk_name: z.string(),
  carrier_name: z.string(),
  trunk_host: z.string(),
  health_status: z.string(),
  /** How the trunk authenticates, so registration state can be read in context. */
  auth_mode: z.enum(['ip', 'digest', 'register']).optional(),
  /** The customer's own trunk (bring your own trunk) rather than one of ours. */
  owned: z.boolean().optional(),
  /** Live state from the switch (carriers.ts TrunkLiveSchema). */
  live: TrunkLiveSchema.nullable().optional(),
  direction: z.enum(['inbound', 'outbound', 'both']),
  priority: z.number().int(),
  is_enabled: z.boolean(),
  override_caller_id: E164Schema.nullable(),
  max_concurrent_calls: z.number().int().nullable(),
  created_at: TimestampSchema,
});
export type TenantTrunk = z.infer<typeof TenantTrunkSchema>;

/**
 * The trunks an account's calls use, as the CUSTOMER may see them.
 *
 * Which carrier carries them and whether it is up right now -- never the host,
 * the credentials or the carrier's own replies, which are ours to see. Health
 * and registration are the switch's live measurements, reduced to what a
 * customer can act on (call their provider, or not).
 */
export const TenantTrunkStatusSchema = z.object({
  id: IdSchema,
  trunk_id: IdSchema,
  trunk_name: z.string(),
  carrier_name: z.string(),
  auth_mode: z.enum(['ip', 'digest', 'register']),
  trunk_status: z.enum(['active', 'disabled', 'draining']),
  direction: z.enum(['inbound', 'outbound', 'both']),
  priority: z.number().int(),
  is_enabled: z.boolean(),
  health: z.enum(['up', 'degraded', 'down', 'unknown']),
  /** 'not_required' for a trunk that does not register with its carrier. */
  registration: z.enum(['registered', 'registering', 'not_registered', 'failing', 'not_required', 'unknown']),
  /** Network round trip to the carrier, and the share of checks answered in 24 h. */
  latency_ms: z.number().nullable(),
  uptime_24h_pct: z.number().nullable(),
  health_since: TimestampSchema.nullable(),
  last_register_at: TimestampSchema.nullable(),
  /** When the switch last reported; null when it never has. */
  reported_at: TimestampSchema.nullable(),
});
export type TenantTrunkStatus = z.infer<typeof TenantTrunkStatusSchema>;

export const TenantTrunkUpsertSchema = z.object({
  trunk_id: IdSchema,
  direction: z.enum(['inbound', 'outbound', 'both']).default('both'),
  /** Lower runs first. Unique per (tenant, direction) so order is never ambiguous. */
  priority: z.number().int().min(1).max(999).default(100),
  is_enabled: z.boolean().default(true),
  override_caller_id: E164Schema.nullable().optional(),
  max_concurrent_calls: z.number().int().min(1).max(1000).nullable().optional(),
}).strict();
